import {
  type BriefCahier,
  type CahierResume,
  type ClotureApercu,
  type Comparaison,
  cleNormale,
  type Demande,
  type Jugement,
  type LigneComparaison,
  LONGUEUR_CLE,
  NOMS_RESERVES,
  type PageResume,
  type Variante,
} from "./contract";
import { compter } from "./format";

/**
 * Les libelles du module coloriage, ecrits une fois : la grille, la page de jugement et les
 * points de couleur disent la meme chose de la meme facon. Tout est pur.
 *
 * Les cles du contrat (`a_jeter`, `kdp`, `zimage`, `en_attente`) ne s'affichent jamais telles
 * quelles.
 */

/** L'ordre des trois boutons et de la legende, celui des touches 1, 2 et 3. */
export const JUGEMENTS: readonly Jugement[] = ["bonne", "defaut", "a_jeter"];

/** Le jugement tel qu'il s'ecrit sur un bouton ou un badge. */
export const LIBELLE_JUGEMENT: Readonly<Record<Jugement, string>> = {
  bonne: "Bonne",
  defaut: "Défaut",
  a_jeter: "À jeter",
};

/** La touche de chaque jugement (rangee des chiffres ou pave numerique). */
export const TOUCHE_JUGEMENT: Readonly<Record<Jugement, string>> = {
  bonne: "1",
  defaut: "2",
  a_jeter: "3",
};

/** Une version sans jugement : « à juger », en minuscule comme dans la legende. */
export const A_JUGER = "à juger";

/** Le jugement dans une phrase ou une legende : `bonne`, `défaut`, `à jeter`, `à juger`. */
export function jugementEnClair(jugement: Jugement | null): string {
  return jugement === null ? A_JUGER : LIBELLE_JUGEMENT[jugement].toLocaleLowerCase("fr");
}

/**
 * Le titre d'une page tel que le cahier le numerote : `07 Le cheval du pré`. Le PC pousse le
 * titre sans son numero, le rang le porte.
 */
export function titrePage(rang: number, titre: string): string {
  return `${String(rang).padStart(2, "0")} ${titre}`;
}

/** Le modele dans une table : le nom court que Nicolas emploie. */
export function modeleCourt(modele: CahierResume["modele"]): string {
  if (modele === "zimage") return "Z-Image";
  return modele;
}

/** Le modele sous un prompt : le nom complet. */
export function modeleLong(modele: CahierResume["modele"]): string {
  if (modele === "klein") return "FLUX.2 klein 4B";
  if (modele === "zimage") return "Z-Image Turbo";
  return "workflow ComfyUI";
}

const FORMAT_COURT: Readonly<Record<CahierResume["format"], string>> = {
  kdp: "KDP 8,5 × 11",
  carre: "KDP 8,5 × 8,5",
  a4: "A4",
};

const FORMAT_LONG: Readonly<Record<CahierResume["format"], string>> = {
  kdp: "KDP 8,5 × 11 pouces",
  carre: "KDP carré, 8,5 × 8,5 pouces",
  a4: "A4, 21 × 29,7 cm",
};

const PROPORTIONS: Readonly<
  Record<CahierResume["format"], { readonly largeur: number; readonly hauteur: number }>
> = {
  kdp: { largeur: 850, hauteur: 1100 },
  carre: { largeur: 850, hauteur: 850 },
  a4: { largeur: 210, hauteur: 297 },
};

/** Le format dans une table : `KDP 8,5 × 11`, `KDP 8,5 × 8,5`, `A4`. */
export function formatCourt(format: CahierResume["format"]): string {
  return FORMAT_COURT[format];
}

/** Le format sous le titre d'un cahier : `KDP 8,5 × 11 pouces`, `A4, 21 × 29,7 cm`. */
export function formatLong(format: CahierResume["format"]): string {
  return FORMAT_LONG[format];
}

/**
 * Les proportions d'une page du format, pour reserver la place d'une vignette avant que son
 * image n'arrive : la grille ne connait pas les dimensions des versions, le format oui.
 */
export function proportionsDuFormat(format: CahierResume["format"]): {
  largeur: number;
  hauteur: number;
} {
  return { ...PROPORTIONS[format] };
}

const KO = 1024;
const MO = 1024 * 1024;

/**
 * Un poids en octets, a la francaise : `4,7 Mo`, `350 Ko`, `12 octets`. Mille vingt-quatre
 * octets au kilo, comme les plafonds du module (1,5 Mo = 1 572 864 octets).
 */
export function poids(octets: number): string {
  if (octets >= MO) return `${(octets / MO).toFixed(1).replace(".", ",")} Mo`;
  if (octets >= KO) return `${Math.round(octets / KO)} Ko`;
  return `${octets} ${octets > 1 ? "octets" : "octet"}`;
}

/** Ce que dit la carte d'une page dans la grille, et le ton de son badge. */
export type EtatDePage =
  | { readonly ton: "choisie"; readonly texte: string }
  | { readonly ton: "a_juger"; readonly texte: string; readonly preChoix: string | null }
  | { readonly ton: "sans_bonne"; readonly texte: string }
  | { readonly ton: "a_choisir"; readonly texte: string; readonly preChoix: string | null }
  | { readonly ton: "vide"; readonly texte: string };

/**
 * L'etat d'une page, dans cet ordre : un choix pose l'emporte (c'est lui qui part dans le
 * PDF), puis ce qui reste a juger, puis l'absence de version bonne. Une page toute jugee,
 * avec une bonne mais sans choix, attend encore que Nicolas choisisse : sans choix, le PC
 * monte son pre-choix.
 */
export function etatDePage(page: PageResume): EtatDePage {
  const preChoix =
    page.pre_choix_essai === null ? null : `pré-choix du PC : version ${page.pre_choix_essai}`;
  if (page.choix_essai !== null) {
    return { ton: "choisie", texte: `choisie : version ${page.choix_essai}` };
  }
  if (page.versions.length === 0) return { ton: "vide", texte: "aucune version" };
  if (page.a_juger > 0) return { ton: "a_juger", texte: `${page.a_juger} à juger`, preChoix };
  if (!page.versions.some((version) => version.jugement === "bonne")) {
    return { ton: "sans_bonne", texte: "aucune version bonne" };
  }
  return { ton: "a_choisir", texte: "à choisir", preChoix };
}

// ---------------------------------------------------------------------------------------
// Les demandes, l'agent du PC et la cloture (ticket 3, #152).

/** L'etat d'une demande tel qu'il s'ecrit dans la table. */
export const LIBELLE_ETAT_DEMANDE: Readonly<Record<Demande["etat"], string>> = {
  en_attente: "en attente",
  en_cours: "en cours",
  faite: "faite",
  echouee: "échouée",
  annulee: "annulée",
};

const SECONDE = 1000;
const MINUTE = 60 * SECONDE;
const HEURE = 60 * MINUTE;
const JOUR = 24 * HEURE;

/**
 * Une duree, a la facon des maquettes : `3 s`, `12 min`, `2 h 14`, `2 h`, `3 jours`. Une duree
 * negative (le navigateur en retard sur l'horloge du serveur) vaut zero.
 */
export function duree(millisecondes: number): string {
  const ms = Math.max(0, millisecondes);
  if (ms < MINUTE) return `${Math.floor(ms / SECONDE)} s`;
  if (ms < HEURE) return `${Math.floor(ms / MINUTE)} min`;
  if (ms < JOUR) {
    const heures = Math.floor(ms / HEURE);
    const minutes = Math.floor((ms % HEURE) / MINUTE);
    return minutes === 0 ? `${heures} h` : `${heures} h ${String(minutes).padStart(2, "0")}`;
  }
  return compter(Math.floor(ms / JOUR), "jour", "jours");
}

/** Le temps ecoule depuis un horodatage de la base, lu a l'horloge du navigateur. */
export function depuis(horodatage: string, maintenant: number): string {
  return duree(maintenant - Date.parse(horodatage));
}

/**
 * Les variantes qu'un plan de comparaison emploie, la reference comprise : les valeurs
 * distinctes de `variante` sur la premiere page qui en porte un. Zero pour un rendu ordinaire.
 */
export function variantesDuPlan(pages: readonly Demande["pages"][number][]): number {
  const plan = pages.find((page) => page.plan !== null)?.plan;
  if (plan === undefined || plan === null) return 0;
  return new Set(plan.map((entree) => entree.variante)).size;
}

/**
 * Vrai quand chaque page d'un rendu part de sa version 1 : aucune n'avait encore de version,
 * puisque les numeros sont reserves au-dela de tout numero deja pris. C'est le premier rendu
 * d'un cahier ne dans la console (#271), et le dire « relance » le ferait mentir.
 */
function premierRendu(pages: readonly Pick<Demande["pages"][number], "essais">[]): boolean {
  return pages.length > 0 && pages.every((page) => page.essais[0] === 1);
}

/**
 * Ce que demande une demande : `Relancer 3 versions de 2 pages`, `Monter le PDF`, et pour une
 * comparaison `Comparer 3 variantes` (ticket 4), la reference comptee comme une variante. Le
 * premier rendu d'un cahier dit `Rendre` ; une demande `ecrire` (Claude, #273), `Écrire le
 * sommaire`.
 */
export function resumeDemande(
  demande: Pick<Demande, "type" | "pages" | "versions_par_page" | "total" | "comparaison">,
): string {
  if (demande.type === "monter") return "Monter le PDF";
  if (demande.type === "ecrire") return "Écrire le sommaire";
  if (demande.comparaison !== null) {
    const variantes = variantesDuPlan(demande.pages);
    return variantes > 0
      ? `Comparer ${compter(variantes, "variante", "variantes")}`
      : "Comparer des variantes";
  }
  const verbe = premierRendu(demande.pages) ? "Rendre" : "Relancer";
  if (demande.versions_par_page === null) {
    return `${verbe} ${compter(demande.total, "version", "versions")}`;
  }
  return `${verbe} ${compter(demande.versions_par_page, "version", "versions")} de ${compter(
    demande.pages.length,
    "page",
    "pages",
  )}`;
}

/** Au-dela de trois pages, la ligne d'une demande les compte au lieu de les nommer. */
const PAGES_NOMMEES = 3;

/** Les pages d'une demande : `02 La vache curieuse, 08 La chèvre gourmande et 2 autres`. */
export function pagesDeLaDemande(pages: readonly { rang: number; titre: string }[]): string {
  const titres = pages.slice(0, PAGES_NOMMEES).map((page) => titrePage(page.rang, page.titre));
  const reste = pages.length - titres.length;
  return reste > 0
    ? `${titres.join(", ")} et ${compter(reste, "autre", "autres")}`
    : titres.join(", ");
}

/**
 * Le detail d'une demande, sous son resume. Un rendu ordinaire part du prompt de reference,
 * avec les numeros de version suivants de chaque page : des graines jamais tirees. Une
 * comparaison (ticket 4) dit sa forme, parce que ses pages sont deja nommees sur son ecran.
 */
export function detailDemande(demande: Demande): string {
  if (demande.comparaison !== null) return formeDeLaDemande(demande);
  if (demande.type === "ecrire") return "Claude, lancé par l'agent du PC";
  if (demande.type === "rendre") {
    return `${pagesDeLaDemande(demande.pages)} · prompt de référence, graines jamais tirées`;
  }
  if (demande.resultat !== null) {
    return `${compter(demande.resultat.pages, "page", "pages")}, sur le PC : ${demande.resultat.pdf}`;
  }
  return "tes choix, sinon le pré-choix du PC";
}

/**
 * La version que l'agent rend en ce moment : la premiere qui n'est pas encore faite, dans
 * l'ordre ou il les prend (page par page, numero par numero). Null au-dela de la derniere.
 *
 * `faites` est un compte, pas une position : sur une demande reprise apres un arret, l'agent
 * compte aussi les versions deja presentes sur le PC, qui ne sont pas forcement les
 * premieres de la liste. La phrase peut alors nommer une version voisine de celle qui se
 * rend vraiment. C'est une phrase d'etat, relue toutes les 5 s, et rien n'en depend.
 */
export function versionEnCours(
  demande: Pick<Demande, "pages" | "faites">,
): { readonly rang: number; readonly titre: string; readonly essai: number } | null {
  const suite = demande.pages.flatMap((page) =>
    page.essais.map((essai) => ({ rang: page.rang, titre: page.titre, essai })),
  );
  return suite[demande.faites] ?? null;
}

/**
 * Ce que fait l'agent : `rend 08 La chèvre gourmande, version 5`, `monte le PDF de ...`.
 *
 * Une demande de comparaison ne dit ni la page, ni le numero (ticket 4) : l'agent rend une
 * variante apres l'autre, donc nommer la version en cours reviendrait a dire de quelle variante
 * elle sort, sur un ecran qui n'a rien a en savoir avant le dernier jugement.
 */
export function travailEnClair(demande: Demande): string {
  if (demande.type === "monter") return `monte le PDF de ${demande.cahier.titre}`;
  if (demande.type === "ecrire")
    return `fait écrire à Claude le sommaire de ${demande.cahier.titre}`;
  if (demande.comparaison !== null) return "rend les versions de la comparaison";
  const version = versionEnCours(demande);
  return version === null
    ? "finit la demande"
    : `rend ${titrePage(version.rang, version.titre)}, version ${version.essai}`;
}

/**
 * Ce qu'une relance garde d'une demande echouee ou annulee : `les 4 versions arrivées sont
 * gardées, il en reste 2 à rendre`. Null quand rien n'est arrive.
 */
export function repriseEnClair(demande: Pick<Demande, "faites" | "total">): string | null {
  if (demande.faites === 0) return null;
  const gardees =
    demande.faites > 1
      ? `les ${demande.faites} versions arrivées sont gardées`
      : "la version arrivée est gardée";
  const reste = demande.total - demande.faites;
  return reste > 0 ? `${gardees}, il en reste ${reste} à rendre` : gardees;
}

const RAISONS_DU_PRE_CHOIX: readonly {
  readonly raison: ClotureApercu["pages_au_pre_choix"][number]["raison"];
  readonly singulier: string;
  readonly pluriel: string;
}[] = [
  { raison: "jamais_jugee", singulier: "jamais jugée", pluriel: "jamais jugées" },
  { raison: "sans_bonne", singulier: "sans version bonne", pluriel: "sans version bonne" },
  { raison: "sans_choix", singulier: "à choisir", pluriel: "à choisir" },
];

/**
 * Les pages qui gardent le pre-choix du PC a la cloture, groupees par raison et numerotees
 * comme dans la grille : `Sans choix de ta part, 5 pages gardent le pré-choix du PC : 3
 * jamais jugées (10, 11, 12) et 2 sans version bonne (02, 08).` Null quand chaque page a son
 * choix.
 */
export function preChoixEnClair(pages: ClotureApercu["pages_au_pre_choix"]): string | null {
  if (pages.length === 0) return null;
  const groupes = RAISONS_DU_PRE_CHOIX.flatMap(({ raison, singulier, pluriel }) => {
    const rangs = pages
      .filter((page) => page.raison === raison)
      .map((page) => String(page.rang).padStart(2, "0"));
    if (rangs.length === 0) return [];
    return [`${rangs.length} ${rangs.length > 1 ? pluriel : singulier} (${rangs.join(", ")})`];
  });
  const dernier = groupes.pop() ?? "";
  const liste = groupes.length > 0 ? `${groupes.join(", ")} et ${dernier}` : dernier;
  return `Sans choix de ta part, ${compter(
    pages.length,
    "page garde",
    "pages gardent",
  )} le pré-choix du PC : ${liste}.`;
}

/** Ce que la cloture efface, et ce qu'elle garde. */
export function effacementEnClair(
  apercu: Pick<ClotureApercu, "images_effacees" | "octets_liberes">,
): string {
  const images = apercu.images_effacees;
  if (images === 0) return "Aucune autre image n'est en base : rien n'est effacé.";
  const effacees =
    images > 1
      ? `Les images des ${images} autres versions sont effacées de la console (${poids(
          apercu.octets_liberes,
        )}) ; leurs jugements et leurs mesures restent`
      : `L'image de l'autre version est effacée de la console (${poids(
          apercu.octets_liberes,
        )}) ; son jugement et ses mesures restent`;
  return `${effacees}, pour les taux des variantes. Les PNG restent sur le PC.`;
}

/** Le bilan d'une cloture faite, dit apres coup. */
export function bilanDeCloture(
  bilan: Pick<ClotureApercu, "images_effacees" | "octets_liberes">,
): string {
  if (bilan.images_effacees === 0) return "Cahier clos : aucune image effacée.";
  return `Cahier clos : ${compter(bilan.images_effacees, "image effacée", "images effacées")}, ${poids(
    bilan.octets_liberes,
  )} libérés.`;
}

// ---------------------------------------------------------------------------------------
// Les variantes du prompt et les comparaisons a l'aveugle (ticket 4, #153).

/** L'auteur d'une variante, tel que la table l'ecrit ; la reference vient du paquet. */
export const LIBELLE_AUTEUR: Readonly<Record<Variante["auteur"], string>> = {
  nicolas: "Nicolas",
  claude: "Claude",
};

/** L'auteur d'une ligne du tableau : celui de la variante, `paquet` pour la reference. */
export function auteurEnClair(auteur: Variante["auteur"] | null): string {
  return auteur === null ? "paquet" : LIBELLE_AUTEUR[auteur];
}

/** Le nom d'une variante dans une phrase ou une colonne ; la reference porte le sien. */
export const NOM_REFERENCE = "Référence";

/** Les trois compteurs d'une variante, ou ceux de la reference qui n'a pas de ligne en base. */
export type Taux = Pick<Variante, "comparaisons" | "versions_jugees" | "bonnes">;

/** Le taux de versions bonnes : `6 sur 12 (50 %)`. Vide sans version jugee. */
export function tauxEnClair(taux: Taux): string {
  if (taux.versions_jugees === 0) return "";
  const part = Math.round((taux.bonnes / taux.versions_jugees) * 100);
  return `${taux.bonnes} sur ${taux.versions_jugees} (${part} %)`;
}

/**
 * Ce que la colonne « Versions bonnes » dit d'une variante. Les compteurs ne portent que sur
 * les comparaisons closes (contrat) : une variante deja employee mais dont la comparaison
 * tourne encore n'a donc pas de taux, et la colonne dit pourquoi plutot que d'afficher zero.
 */
export function attenteDuTaux(variante: Pick<Variante, "figee"> & Taux): string | null {
  if (variante.comparaisons > 0 && variante.versions_jugees > 0) return null;
  return variante.figee ? "à la clôture de la comparaison" : "jamais comparée";
}

/** Les comparaisons closes d'une variante : `1 close`, `3 closes`, `aucune`. */
export function comparaisonsEnClair(comparaisons: number): string {
  return comparaisons === 0 ? "aucune" : compter(comparaisons, "close", "closes");
}

/** L'etat d'une variante sous son nom : figee, archivee, ou un brouillon jamais employe. */
export function etatVariante(variante: Pick<Variante, "figee" | "archivee">): string {
  if (variante.archivee) return "archivée";
  return variante.figee ? "figée" : "brouillon";
}

/** Le nombre de sujets qu'une variante remplace : `1 sujet remplacé`, vide s'il n'y en a pas. */
export function sujetsEnClair(sujets: Variante["sujets"]): string {
  const nombre = Object.keys(sujets).length;
  return nombre === 0 ? "" : compter(nombre, "sujet remplacé", "sujets remplacés");
}

/** Le temps qu'un rendu prend sur le PC, mesure le 21 septembre 2026 sur klein 4B. */
export const SECONDES_PAR_RENDU = 18;

/**
 * Ce que coute une comparaison avant de la lancer : `24 rendus, environ 7 minutes sur le PC
 * (18 s le rendu).` Le compte est le produit exact que le backend posera en `total`.
 */
export function coutDeComparaison(
  pages: number,
  versionsParPage: number,
  variantes: number,
): string {
  const rendus = pages * versionsParPage * variantes;
  const minutes = Math.round((rendus * SECONDES_PAR_RENDU) / 60);
  const temps =
    minutes < 1 ? "moins d'une minute" : `environ ${compter(minutes, "minute", "minutes")}`;
  return `${compter(rendus, "rendu", "rendus")}, ${temps} sur le PC (${SECONDES_PAR_RENDU} s le rendu).`;
}

/** Une date de la base, en clair : `22 septembre`. Lue en temps universel, comme `formatDate`. */
const MOIS: readonly string[] = [
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
];

export function jourEnClair(horodatage: string): string {
  const quand = new Date(Date.parse(horodatage));
  if (Number.isNaN(quand.getTime())) return horodatage;
  return `${quand.getUTCDate()} ${MOIS[quand.getUTCMonth()] ?? ""}`.trim();
}

/** Le nom d'une comparaison, qui n'en a pas en base : `Comparaison du 22 septembre`. */
export function titreComparaison(comparaison: Pick<Comparaison, "cree_le">): string {
  return `Comparaison du ${jourEnClair(comparaison.cree_le)}`;
}

/**
 * La forme d'une comparaison : `4 pages × 3 versions × 3 variantes · mêmes graines pour chaque
 * variante`. Sert au sous-titre de son ecran et au detail de sa demande.
 */
export function formeDeComparaison(
  pages: number,
  versionsParPage: number,
  variantes: number,
): string {
  return `${compter(pages, "page", "pages")} × ${compter(
    versionsParPage,
    "version",
    "versions",
  )} × ${compter(variantes, "variante", "variantes")} · mêmes graines pour chaque variante`;
}

/** La forme d'une comparaison, lue sur sa demande de rendu (table des demandes). */
export function formeDeLaDemande(demande: Pick<Demande, "pages" | "versions_par_page">): string {
  return formeDeComparaison(
    demande.pages.length,
    demande.versions_par_page ?? 0,
    variantesDuPlan(demande.pages),
  );
}

/** L'avancement des jugements : `12 sur 36 jugées`. */
export function avancementDesJugements(comparaison: Pick<Comparaison, "jugees" | "total">): string {
  return `${comparaison.jugees} sur ${comparaison.total} jugées`;
}

/**
 * Ou en est le rendu, tant qu'il n'est pas fini : `14 versions sur 36 rendues, le jugement
 * s'ouvre quand tout est là.`
 *
 * Le jugement a l'aveugle n'ouvre qu'a `arrivees === total`, et ce n'est pas une precaution de
 * confort : l'agent rend une variante apres l'autre et pousse au fil de l'eau, donc juger
 * pendant le rendu, c'est juger une serie entiere issue d'une seule variante, reconnaissable
 * sans rien inspecter.
 */
export function avancementDuRendu(comparaison: Pick<Comparaison, "arrivees" | "total">): string {
  const rendues = comparaison.arrivees > 1 ? "rendues" : "rendue";
  return `${compter(comparaison.arrivees, "version", "versions")} sur ${
    comparaison.total
  } ${rendues}, le jugement s'ouvre quand tout est là.`;
}

/** Pourquoi le rendu ferme le jugement : la phrase de l'encadre, sous l'avancement. */
export const RENDU_AVANT_JUGEMENT =
  "L'agent rend une variante après l'autre et pousse au fil de l'eau : juger avant la fin, ce serait juger une série entière issue d'une même variante, reconnaissable sans rien inspecter.";

/** Le rang de passage d'une version a l'aveugle : `version 31 sur 36`. */
export function passageEnClair(passage: number, total: number): string {
  return `version ${passage} sur ${total}`;
}

/** L'ecart a partir duquel une comparaison dit quelque chose : moins, c'est du bruit. */
export const ECART_QUI_COMPTE = 4;

/**
 * Ce que dit le tableau, en une phrase. La meilleure variante d'abord, comparee a la
 * reference sur les memes graines, puis les autres, puis la regle de lecture : sur douze
 * versions par variante, un ecart de un ou deux ne prouve rien.
 *
 * Trois fins, parce qu'un ecart negatif n'est pas un ecart : quand aucune variante ne fait
 * mieux que la reference, la phrase le dit et s'arrete la, au lieu d'annoncer « un ecart de
 * -4 ».
 */
export function lectureDuTableau(tableau: readonly LigneComparaison[]): string {
  const reference = tableau.find((une) => une.variante === null);
  const autres = tableau.filter((une) => une.variante !== null);
  if (autres.length === 0) return "Cette comparaison n'a qu'une colonne : rien à comparer.";
  const classees = [...autres].sort((a, b) => b.bonnes - a.bonnes);
  const meilleure = classees[0] as LigneComparaison;
  const total = meilleure.total;
  const suite = classees
    .slice(1)
    .map((une) => `« ${une.variante?.nom} » en donne ${une.bonnes}.`)
    .join(" ");
  const rappel = `Sur ${total} versions par variante, un écart de 1 ou 2 ne prouve rien`;
  const phrase = (tete: string, regle: string): string =>
    [tete, suite, regle].filter((morceau) => morceau !== "").join(" ");

  if (reference === undefined) {
    return phrase(
      `« ${meilleure.variante?.nom} » donne ${meilleure.bonnes} versions bonnes sur ${total}.`,
      `${rappel}.`,
    );
  }
  const ecart = meilleure.bonnes - reference.bonnes;
  if (ecart <= 0) {
    return phrase(
      `La référence donne ${reference.bonnes} versions bonnes sur ${total}, et aucune variante ne fait mieux : « ${meilleure.variante?.nom} » en donne ${meilleure.bonnes}.`,
      `${rappel} : le prompt du paquet tient, rien n'invite à le changer.`,
    );
  }
  return phrase(
    `« ${meilleure.variante?.nom} » donne ${meilleure.bonnes} versions bonnes sur ${total}, contre ${reference.bonnes} pour la référence sur les mêmes graines.`,
    ecart >= ECART_QUI_COMPTE
      ? `${rappel} : celui-ci en vaut ${ecart}, et mérite une seconde comparaison sur d'autres pages.`
      : `${rappel} : celui-ci en vaut ${ecart}, il faut d'autres pages pour trancher.`,
  );
}

/** Ce que clore une comparaison efface, avant de le faire. */
export function effacementDeComparaison(comparaison: Pick<Comparaison, "total">): string {
  return `Clore la comparaison efface les images de ses ${comparaison.total} versions. Les jugements restent : ils font le taux de chaque variante.`;
}

/** Le bilan d'une comparaison close, dit apres coup. */
export function bilanDeComparaison(
  bilan: Pick<ClotureApercu, "images_effacees" | "octets_liberes">,
): string {
  if (bilan.images_effacees === 0) return "Comparaison close : aucune image effacée.";
  return `Comparaison close : ${compter(
    bilan.images_effacees,
    "image effacée",
    "images effacées",
  )}, ${poids(bilan.octets_liberes)} libérés.`;
}

// ---------------------------------------------------------------------------------------
// Creer depuis la console (#271) : le brief, le sommaire d'un brouillon, son lancement.

/** Un niveau d'age, tel que `coloring.prompts.NIVEAUX` le nomme. */
export type Niveau = BriefCahier["niveau"];

/** Les trois niveaux, du plus jeune au plus grand : l'ordre des boutons et des listes. */
export const NIVEAUX: readonly Niveau[] = ["tout-petits", "enfants", "grands"];

/** Le niveau d'un cahier neuf : 6 a 8 ans, celui que `coloring.prompts` prend par defaut. */
export const NIVEAU_PAR_DEFAUT: Niveau = "enfants";

/** L'age d'un niveau, tel que Nicolas le dit. Les cles du contrat ne s'affichent pas. */
export const LIBELLE_NIVEAU: Readonly<Record<Niveau, string>> = {
  "tout-petits": "3 à 5 ans",
  enfants: "6 à 8 ans",
  grands: "9 ans et plus",
};

/**
 * Ou en est un brouillon, sous son badge a l'accueil (spec, section 3.1) : `sommaire à
 * écrire`, `sommaire en cours, 12 pages` tant qu'une page n'a pas de sujet, `sommaire prêt, à
 * lancer` sinon. Null pour un cahier ouvert ou clos, que ses colonnes disent deja.
 */
export function etatCahierCourt(
  cahier: Pick<CahierResume, "etat" | "pages" | "pages_sans_sujet">,
): string | null {
  if (cahier.etat !== "brouillon") return null;
  if (cahier.pages === 0) return "sommaire à écrire";
  if (cahier.pages_sans_sujet === 0) return "sommaire prêt, à lancer";
  return `sommaire en cours, ${compter(cahier.pages, "page", "pages")}`;
}

/**
 * Pourquoi la cle d'un cahier neuf ne passe pas, ou null quand `cleNormale` l'accepte. Le
 * contrat decide (c'est le nom du dossier que l'agent creera sur le PC) ; seul le message est
 * d'ici.
 */
export function refusDeCleCahier(cle: string): string | null {
  if (cle === "") return "La clé est vide : elle se remplit depuis le titre.";
  if (cleNormale(cle)) return null;
  if (NOMS_RESERVES.has(cle)) {
    return "Ce nom est réservé par Windows : le dossier ne pourrait pas se créer.";
  }
  if (cle.length > LONGUEUR_CLE) return `${LONGUEUR_CLE} caractères au plus.`;
  return "Minuscules et chiffres, séparés par un seul tiret, ni tiret au début ni à la fin : c'est le nom du dossier sur le PC.";
}

/** Des rangs de page dans une phrase : `Page sans sujet : 3.`, `Pages sans sujet : 3, 7.` */
export function pagesEnClair(rangs: readonly number[], quoi: string): string {
  return `${rangs.length > 1 ? "Pages" : "Page"} ${quoi} : ${rangs.join(", ")}.`;
}

/**
 * Le temps d'un rendu sur le PC, controles compris : 17,7 s, mesure le 21 septembre 2026 sur
 * la ferme en klein (point 40 de `docs/etat-du-lab.md`). `SECONDES_PAR_RENDU` en est l'arrondi
 * que l'ecran des comparaisons affiche.
 */
export const SECONDES_PAR_RENDU_PC = 17.7;

/**
 * Ce que coute le lancement d'un brouillon sur le PC, avant de cliquer : `24 pages × 3 versions
 * = 72 rendus sur le PC, environ 21 minutes`. Le compte est celui que le backend posera en
 * `total` ; klein ne coute rien.
 */
export function estimationPC(pages: number, versionsParPage: number): string {
  const rendus = pages * versionsParPage;
  const minutes = Math.round((rendus * SECONDES_PAR_RENDU_PC) / 60);
  const temps =
    minutes < 1
      ? "moins d'une minute"
      : minutes < 60
        ? `environ ${compter(minutes, "minute", "minutes")}`
        : `environ ${duree(minutes * MINUTE)}`;
  return `${compter(pages, "page", "pages")} × ${compter(
    versionsParPage,
    "version",
    "versions",
  )} = ${compter(rendus, "rendu", "rendus")} sur le PC, ${temps}`;
}

/** Ce qu'un lancement a pose, dit apres coup sur la grille du cahier lance. */
export function lancementEnClair(
  demande: Pick<Demande, "pages" | "versions_par_page" | "total">,
): string {
  const forme =
    demande.versions_par_page === null
      ? compter(demande.pages.length, "page", "pages")
      : `${compter(demande.versions_par_page, "version", "versions")} de ${compter(
          demande.pages.length,
          "page",
          "pages",
        )}`;
  return `Rendu lancé : ${compter(demande.total, "rendu", "rendus")} sur le PC (${forme}). L'agent du PC le prend dès qu'il est en ligne.`;
}
